import { Helmet } from 'react-helmet-async';
import { Outlet, matchPath, useLocation } from 'react-router-dom';

import { PrivacyToggleProvider } from '@eluve/blocks';
import {
  Icon,
  Layout,
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuNavButton,
} from '@eluve/components';
import { useTranslation } from '@eluve/i18n';
import { MedplumProvider } from '@eluve/medplum-client';
import { useIsPostHogFeatureFlagEnabled } from '@eluve/posthog-react';
import {
  useAssignedTenantIdFromParams,
  useIsPromptTemplateCreator,
  useIsTemplatePublisher,
  useIsTenantAdminFromSession,
} from '@eluve/session-helpers';
import { ChatAccessGate, FeatureFlaggedComponent } from '@eluve/smart-blocks';

import { appConfig } from '../../../../../config';
import { TenantUserSettings } from '../../../TenantUserSettings';
import { useIsCarePlanFeatureEnabled } from '../../CarePlanFeatureGate';
import { ImpersonationExpiryMonitor } from '../ImpersonationExpiryMonitor';
import { TranscriptionWidget } from '../appointments/machines/transcription/components/TranscriptionWidget';

const SettingsNavItem = ({ label, path }: { label: string; path: string }) => {
  const tenantId = useAssignedTenantIdFromParams();
  const { pathname } = useLocation();

  const to = `/organizations/${tenantId}/settings/${path}`;

  // Wildcard match keeps the tab active while a drill-in sub-route is open
  const isActive =
    matchPath(to, pathname) != null || matchPath(`${to}/*`, pathname) != null;

  return (
    <SidebarMenuItem>
      <SidebarMenuNavButton to={to} isActive={isActive}>
        <span>{label}</span>
      </SidebarMenuNavButton>
    </SidebarMenuItem>
  );
};

// Rendered inside <TenantUserSettings> so useTranslation reads the
// settings-driven I18nProvider language (SettingsShell itself sits above it).
const SettingsSidebar = () => {
  const tenantId = useAssignedTenantIdFromParams();
  const { t } = useTranslation();

  const isTenantAdmin = useIsTenantAdminFromSession();
  const hasInsights = useIsPostHogFeatureFlagEnabled(
    'EXTENSION_PREVISIT_INSIGHTS',
  );
  const isPromptTemplateCreator = useIsPromptTemplateCreator();
  const isTemplatePublisher = useIsTemplatePublisher();
  const hasTemplates = isPromptTemplateCreator || isTemplatePublisher;
  const hasCarePlans = useIsCarePlanFeatureEnabled();

  return (
    <Sidebar variant="inset" collapsible="offcanvas">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuNavButton
              to={`/organizations/${tenantId}/home`}
              className="justify-center pr-6"
            >
              <Icon name="ChevronLeft" size="auto" />
              <span>{t('settings.nav.backToApp')}</span>
            </SidebarMenuNavButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>{t('settings.nav.account')}</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <SettingsNavItem
                label={t('settings.nav.userSettings')}
                path="user"
              />
              <SettingsNavItem
                label={t('settings.nav.audioDisplay')}
                path="audio"
              />
              <SettingsNavItem
                label={t('settings.nav.shortcuts')}
                path="shortcuts"
              />
              <SettingsNavItem
                label={t('settings.nav.appGrants')}
                path="app-grants"
              />
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        <SidebarGroup>
          <SidebarGroupLabel>{t('settings.nav.preferences')}</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <SettingsNavItem label={t('settings.nav.note')} path="note" />
              {hasTemplates && (
                <SettingsNavItem
                  label={t('settings.nav.templates')}
                  path="templates"
                />
              )}
              <ChatAccessGate>
                <SettingsNavItem label={t('settings.nav.chat')} path="chat" />
              </ChatAccessGate>
              {hasInsights && (
                <SettingsNavItem
                  label={t('settings.nav.insights')}
                  path="insights"
                />
              )}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        {isTenantAdmin && (
          <SidebarGroup>
            <SidebarGroupLabel>
              {t('settings.nav.organization')}
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                <SettingsNavItem
                  label={t('settings.nav.general')}
                  path="organization"
                />
                <SettingsNavItem
                  label={t('settings.nav.members')}
                  path="members"
                />
                <FeatureFlaggedComponent flag="SESSION_TYPES">
                  <SettingsNavItem
                    label={t('settings.nav.sessionTypes')}
                    path="session-types"
                  />
                </FeatureFlaggedComponent>
                <FeatureFlaggedComponent flag="LOCATIONS">
                  <SettingsNavItem
                    label={t('settings.nav.locations')}
                    path="locations"
                  />
                </FeatureFlaggedComponent>
                <FeatureFlaggedComponent flag="USER_GROUPS">
                  <SettingsNavItem
                    label={t('settings.nav.userGroups')}
                    path="user-groups"
                  />
                </FeatureFlaggedComponent>
                <FeatureFlaggedComponent flag="ENABLE_TENANT_ADMIN_REGENERATE_NOTE">
                  <SettingsNavItem
                    label={t('settings.nav.noteRecovery')}
                    path="regenerate-notes"
                  />
                </FeatureFlaggedComponent>
                {hasCarePlans && (
                  <FeatureFlaggedComponent flag="MEDPLUM">
                    <SettingsNavItem
                      label={t('settings.nav.carePlans')}
                      path="care-plans"
                    />
                  </FeatureFlaggedComponent>
                )}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>
    </Sidebar>
  );
};

/**
 * Full-screen takeover shell for the consolidated settings experience:
 * settings-only sidebar (grouped, gated nav) + per-tab pages via Outlet.
 */
export const SettingsShell = () => {
  return (
    <>
      <Helmet>
        <title>Settings | Eluve</title>
      </Helmet>
      <TenantUserSettings>
        <Layout>
          <SettingsSidebar />
          <PrivacyToggleProvider>
            <ImpersonationExpiryMonitor />
            <MedplumProvider baseUrl={appConfig.VITE_MEDPLUM_BASE_URL}>
              <Outlet />
            </MedplumProvider>
          </PrivacyToggleProvider>
          <TranscriptionWidget />
        </Layout>
      </TenantUserSettings>
    </>
  );
};
